import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {toSignal} from '@angular/core/rxjs-interop';

import {Store} from '@ngrx/store';
import {TrackerMetric} from 'src/app/core/model/tracker';
import {AddMetricToTrackerAction, UpdateTrackerMetricAction} from 'src/app/core/store/trackers/trackers.action';
import {MessageService} from 'src/app/services/message.service';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {TrackersService} from 'src/app/shared/services/trackers.service';
import {TrackerDetailMetricComponent} from '../metric/tracker-detail-metric.component';

interface DialogData {
  metric?: TrackerMetric;
  trackerId: string;
}

@Component({
  selector: 'add-metric-dialog',
  standalone: true,
  imports: [OphIconModule, TrackerDetailMetricComponent, OphButtonModule],
  templateUrl: './add-metric-dialog.component.html',
  styleUrl: './add-metric-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddMetricDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AddMetricDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly trackersService = inject(TrackersService);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);

  readonly loading = signal(false);
  readonly dateTimePickerOpen = signal(false);
  readonly metricForm: FormGroup = this.trackersService.getMetricsFormGroup(this.data?.metric || ({} as TrackerMetric));

  // Convert form value changes to a signal
  private readonly formValue = toSignal(this.metricForm.valueChanges, {initialValue: this.metricForm.value});

  // Compute validity based on the reactive form value
  readonly valid = computed(() => this.trackersService.isMetricFormValid(this.formValue()));

  onClose() {
    this.dialogRef.close();
  }

  onDateTimePickerChange(isOpen: boolean) {
    this.dateTimePickerOpen.set(isOpen);
  }

  onAddMetric() {
    this.loading.set(true);
    const metricDto = this.trackersService.formatMetricDto(this.metricForm);

    if (this.data.metric) {
      metricDto._id = this.data.metric._id;
      this.updateMetric(metricDto);
    } else {
      this.addNewMetric(metricDto);
    }
  }

  private addNewMetric(metric: TrackerMetric) {
    this.store.dispatch(
      new AddMetricToTrackerAction({
        trackerId: this.data.trackerId,
        metric,
        onSuccess: () => this.onSuccess(),
        onFailure: err => this.onFailure(err),
      })
    );
  }

  private updateMetric(metric: TrackerMetric) {
    this.store.dispatch(
      new UpdateTrackerMetricAction({
        trackerId: this.data.trackerId,
        metric,
        onSuccess: () => this.onSuccess(),
        onFailure: err => this.onFailure(err),
      })
    );
  }

  private onSuccess() {
    this.onClose();
    this.loading.set(false);
  }

  private onFailure(err: Error) {
    this.loading.set(false);
    this.messageService.add(err.message || 'There was a problem adding the metric.');
  }
}
